import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  User, 
  MapPin, 
  Star, 
  Clock, 
  Award,
  Search,
  Filter,
  MessageSquare,
  Eye,
  CheckCircle,
  Bookmark,
  BookmarkCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { applyPageMetadata } from '../utils/seo';

function BartendersPage({ ownerMode = false }) {
  const { user, isBarOwner } = useAuth();
  const navigate = useNavigate();
  const [bartenders, setBartenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [experienceFilter, setExperienceFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [sortByDistance, setSortByDistance] = useState(false);
  const [availableTonightOnly, setAvailableTonightOnly] = useState(false);
  const [savedBartenderIds, setSavedBartenderIds] = useState([]);
  const [savingBartenderIds, setSavingBartenderIds] = useState({});
  const [hasLocalResults, setHasLocalResults] = useState(false);
  const [ownerBaseLocation, setOwnerBaseLocation] = useState(null);

  const ownerRoutesEnabled = Boolean(user && isBarOwner);
  const isOwnerBrowse = ownerMode === true;
  const showOwnerActions = isOwnerBrowse && ownerRoutesEnabled;

  useEffect(() => {
    const cleanup = applyPageMetadata({
      title: isOwnerBrowse ? 'Owner Bartender Marketplace | BarsConnect' : 'Browse Bartender Profiles | BarsConnect',
      description:
        isOwnerBrowse
          ? 'Owner-only bartender marketplace with search, filters, favorites, and direct messaging.'
          : 'Browse bartender profiles and discover available talent.',
      canonicalUrl: isOwnerBrowse ? 'https://barsconnect.com/owner/browse-bartenders' : 'https://barsconnect.com/bartenders',
      robots: 'index,follow',
    });

    return cleanup;
  }, [isOwnerBrowse]);

  const loadSavedBartenders = async () => {
    if (!ownerRoutesEnabled) {
      setSavedBartenderIds([]);
      return;
    }
    try {
      const response = await axios.get('/owners/saved-bartender-ids');
      setSavedBartenderIds(response.data?.saved_bartender_ids || []);
    } catch (error) {
      console.error('Failed to load saved bartenders:', error);
    }
  };

  useEffect(() => {
    fetchBartenders();
  }, [availableTonightOnly, sortByDistance, userLocation?.lat, userLocation?.lon]);

  useEffect(() => {
    loadSavedBartenders();
  }, [ownerRoutesEnabled]);

  useEffect(() => {
    const loadOwnerLocationContext = async () => {
    if (!ownerRoutesEnabled || !isOwnerBrowse) {
        setOwnerBaseLocation(null);
        return;
      }

      try {
        const response = await axios.get('/owner/settings');
        const location = response.data?.location || {};
        const city = (location.city || '').trim();
        const state = (location.state || '').trim();

        setOwnerBaseLocation({ city, state });
        if (city || state) {
          setSortByDistance(true);
        }

        const latitude = Number(location.latitude);
        const longitude = Number(location.longitude);
        if (!Number.isNaN(latitude) && !Number.isNaN(longitude)) {
          setUserLocation({ lat: latitude, lon: longitude, city, state });
        }
      } catch (error) {
        console.error('Unable to load owner location context:', error);
      }
    };

    loadOwnerLocationContext();
  }, [ownerRoutesEnabled, isOwnerBrowse]);

  const fetchBartenders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/bartenders', {
        params: {
          limit: 50,
          available_tonight_only: availableTonightOnly,
          local_first: sortByDistance,
          owner_lat: userLocation?.lat,
          owner_lon: userLocation?.lon,
          radius_miles: 25,
        }
      });
      setBartenders(response.data);
    } catch (error) {
      console.error('Failed to fetch bartenders:', error);
      toast.error('Failed to fetch bartenders');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = (bartenderId) => {
    if (!user) {
      toast.info('Create a free owner account to message bartenders, view full profiles, and hire faster.');
      navigate('/register');
      return;
    }

    if (!isBarOwner) {
      toast.error('Only bar owners can message bartenders.');
      return;
    }

    // Navigate to messages page with the bartender ID
    navigate(`/messages?user=${bartenderId}`);
  };

  const handleViewProfile = (bartenderId) => {
    // Navigate to detailed profile view
    navigate(`/bartenders/${bartenderId}`);
  };

  const toggleSaveBartender = async (bartenderUserId) => {
    if (!ownerRoutesEnabled) {
      toast.info('Create a free owner account to save bartenders and contact your shortlist.');
      navigate('/register');
      return;
    }

    const isSaved = savedBartenderIds.includes(bartenderUserId);
    setSavingBartenderIds((prev) => ({ ...prev, [bartenderUserId]: true }));
    try {
      if (isSaved) {
        await axios.delete(`/owners/saved-bartenders/${bartenderUserId}`);
        setSavedBartenderIds((prev) => prev.filter((id) => id !== bartenderUserId));
        toast.success('Removed from Saved Bartenders');
      } else {
        await axios.post(`/owners/saved-bartenders/${bartenderUserId}`);
        setSavedBartenderIds((prev) => (prev.includes(bartenderUserId) ? prev : [...prev, bartenderUserId]));
        toast.success('Saved to Saved Bartenders');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update saved bartenders');
    } finally {
      setSavingBartenderIds((prev) => ({ ...prev, [bartenderUserId]: false }));
    }
  };

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Radius of Earth in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return distance; // Distance in miles
  };

  // Find bartenders near the bar owner's location
  const findBartendersNearMe = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setDetectingLocation(true);
    
    try {
      // Get bar owner's GPS coordinates
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;
      setUserLocation({ lat: latitude, lon: longitude });
      setSortByDistance(true);

      // Get city/state for display
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`
      );
      const data = await response.json();
      const city = data.address?.city || data.address?.town || data.address?.village || '';
      const state = data.address?.state || '';

      toast.success(`Finding bartenders near ${city}${state ? ', ' + state : ''}...`);
      
      // Trigger re-fetch with location
      fetchBartenders();
    } catch (error) {
      console.error('Location detection failed:', error);
      if (error.code === 1) {
        toast.error('Location access denied. Please enable location permissions.');
      } else if (error.code === 2) {
        toast.error('Location unavailable. Please check your device settings.');
      } else if (error.code === 3) {
        toast.error('Location request timed out. Please try again.');
      } else {
        toast.error('Failed to detect location. Please try again.');
      }
    } finally {
      setDetectingLocation(false);
    }
  };

  const clearLocationFilter = () => {
    setUserLocation(null);
    setSortByDistance(false);
    toast.info('Location filter cleared');
  };

  // Filter and sort bartenders
  const filteredBartenders = useMemo(() => {
    const prepared = bartenders
      .map((bartender) => {
        const clone = { ...bartender };
        if (userLocation && clone.location?.latitude != null && clone.location?.longitude != null) {
          const lat = Number(clone.location.latitude);
          const lon = Number(clone.location.longitude);
          if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
            clone.distance = calculateDistance(userLocation.lat, userLocation.lon, lat, lon);
          }
        }
        return clone;
      })
      .filter(bartender => {
      const matchesSearch = 
        bartender.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bartender.user?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bartender.skills?.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase())) ||
        bartender.bio?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesExperience = experienceFilter === 'all' || 
        (experienceFilter === '0-2' && bartender.experience_years <= 2) ||
        (experienceFilter === '3-5' && bartender.experience_years >= 3 && bartender.experience_years <= 5) ||
        (experienceFilter === '5+' && bartender.experience_years > 5);
      
      const matchesLocation = locationFilter === 'all' || 
        bartender.location?.city?.toLowerCase().includes(locationFilter.toLowerCase());

      const matchesAvailableTonight = !availableTonightOnly || bartender.available_tonight === true;
      
      return matchesSearch && matchesExperience && matchesLocation && matchesAvailableTonight;
    })

    if (sortByDistance) {
      const localOnly = prepared.filter((bartender) => bartender.distance !== undefined && bartender.distance <= 25);
      const sortedLocals = [...localOnly].sort((a, b) => (a.distance || 0) - (b.distance || 0));
      if (sortedLocals.length > 0) {
        return sortedLocals;
      }

      const ownerCity = (ownerBaseLocation?.city || '').toLowerCase().trim();
      const ownerState = (ownerBaseLocation?.state || '').toLowerCase().trim();

      if (ownerCity || ownerState) {
        const localCityStateMatches = prepared.filter((bartender) => {
          const bartenderCity = (bartender.location?.city || '').toLowerCase().trim();
          const bartenderState = (bartender.location?.state || '').toLowerCase().trim();
          const cityMatch = ownerCity && bartenderCity && bartenderCity === ownerCity;
          const stateMatch = ownerState && bartenderState && bartenderState === ownerState;
          return cityMatch || stateMatch;
        });

        if (localCityStateMatches.length > 0) {
          return [...localCityStateMatches].sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
        }
      }

      const broadFallback = [...prepared].sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
      return broadFallback;
    }

    return [...prepared].sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
  }, [bartenders, searchTerm, experienceFilter, locationFilter, availableTonightOnly, userLocation, sortByDistance, ownerBaseLocation]);

  useEffect(() => {
    if (!sortByDistance) {
      setHasLocalResults(false);
      return;
    }
    const localByDistanceCount = filteredBartenders.filter((bartender) => bartender.distance !== undefined && bartender.distance <= 25).length;

    const ownerCity = (ownerBaseLocation?.city || '').toLowerCase().trim();
    const ownerState = (ownerBaseLocation?.state || '').toLowerCase().trim();
    const localByCityStateCount = filteredBartenders.filter((bartender) => {
      const bartenderCity = (bartender.location?.city || '').toLowerCase().trim();
      const bartenderState = (bartender.location?.state || '').toLowerCase().trim();
      const cityMatch = ownerCity && bartenderCity && bartenderCity === ownerCity;
      const stateMatch = ownerState && bartenderState && bartenderState === ownerState;
      return cityMatch || stateMatch;
    }).length;

    setHasLocalResults(localByDistanceCount > 0 || localByCityStateCount > 0);
  }, [sortByDistance, filteredBartenders, ownerBaseLocation]);

  if (isOwnerBrowse && !user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="owner-browse-bartenders-login-required-state">
        <Card className="bg-white/5 backdrop-blur-md border border-white/10">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-semibold text-white mb-2">Owner Access Required</h2>
            <p className="text-muted-foreground mb-4">Please log in as a bar owner to browse, save, and message bartenders.</p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button asChild data-testid="owner-browse-login-btn"><a href="/login">Log In</a></Button>
              <Button asChild variant="outline" data-testid="owner-browse-register-btn"><a href="/register">Create Owner Account</a></Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isOwnerBrowse && user && !isBarOwner) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="owner-browse-bartenders-role-restricted-state">
        <Card className="bg-white/5 backdrop-blur-md border border-white/10">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-semibold text-white mb-2">Bar Owner Access Only</h2>
            <p className="text-muted-foreground">This page is available only for bar owner accounts.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="bartenders-page">
      {/* Header with Find Near Me Button */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-2 font-['Barlow_Condensed']">
            Find Bartenders
          </h1>
          <p className="text-lg text-muted-foreground">
            {sortByDistance 
              ? hasLocalResults
                ? `${filteredBartenders.length} bartender${filteredBartenders.length !== 1 ? 's' : ''} within 25 miles`
                : `No local results within 25 miles — showing best broader matches`
              : isOwnerBrowse
                ? 'Search, filter, save favorites, and message bartenders directly'
                : 'Browse public bartender profiles and view profile details'
            }
          </p>
        </div>
        
        {/* Find Near Me Button */}
        <div className="flex gap-2">
          {!user ? (
            <Button asChild variant="outline" size="lg" data-testid="public-bartenders-create-owner-account-top-btn">
              <a href="/register">Create Free Owner Account</a>
            </Button>
          ) : isBarOwner && isOwnerBrowse ? (
            <>
              <Button asChild variant="outline" size="lg" className="h-12 min-h-[48px] px-5" data-testid="browse-saved-bartenders-top-btn">
                <a href="/saved-bartenders">Saved Bartenders</a>
              </Button>
              {userLocation ? (
                <Button
                  onClick={clearLocationFilter}
                  variant="outline"
                  size="lg"
                  className="h-12 min-h-[48px] px-5"
                >
                  Clear Location
                </Button>
              ) : (
                <Button
                  onClick={findBartendersNearMe}
                  disabled={detectingLocation}
                  variant="default"
                  size="lg"
                  className="bg-primary hover:bg-[hsl(var(--primary-light))] h-12 min-h-[48px] px-5"
                >
                  <MapPin className="mr-2 h-5 w-5" />
                  {detectingLocation ? 'Detecting...' : 'Find Near Me'}
                </Button>
              )}
            </>
          ) : isBarOwner ? (
            <Button asChild variant="outline" size="lg" data-testid="open-owner-browse-marketplace-top-btn">
              <a href="/owner/browse-bartenders">Open Owner Bartender Marketplace</a>
            </Button>
          ) : userLocation ? (
            <Button
              onClick={clearLocationFilter}
              variant="outline"
              size="lg"
            >
              Clear Location
            </Button>
          ) : (
            <Button
              onClick={findBartendersNearMe}
              disabled={detectingLocation}
              variant="default"
              size="lg"
              className="bg-primary hover:bg-[hsl(var(--primary-light))]"
            >
              <MapPin className="mr-2 h-5 w-5" />
              {detectingLocation ? 'Detecting...' : 'Find Near Me'}
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="bg-white/5 backdrop-blur-md border border-white/5 mb-6 sticky top-16 z-20" data-testid="bartender-filters-sticky-card">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, skills, or bio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="bartender-search-input"
                />
              </div>
            </div>
            <div className="lg:w-48">
              <Select value={experienceFilter} onValueChange={setExperienceFilter}>
                <SelectTrigger data-testid="experience-filter" className="h-12">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Experience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Experience</SelectItem>
                  <SelectItem value="0-2">0-2 years</SelectItem>
                  <SelectItem value="3-5">3-5 years</SelectItem>
                  <SelectItem value="5+">5+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="lg:w-48">
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger data-testid="location-filter" className="h-12">
                  <MapPin className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  <SelectItem value="san francisco">San Francisco</SelectItem>
                  <SelectItem value="new york">New York</SelectItem>
                  <SelectItem value="los angeles">Los Angeles</SelectItem>
                  <SelectItem value="chicago">Chicago</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="lg:w-56 flex items-center justify-between rounded-md border border-white/10 px-3 py-2 h-12" data-testid="available-tonight-filter-container">
              <label htmlFor="available-tonight-only-filter" className="text-sm text-muted-foreground">
                Available Tonight Only
              </label>
              <Switch
                id="available-tonight-only-filter"
                checked={availableTonightOnly}
                onCheckedChange={setAvailableTonightOnly}
                data-testid="available-tonight-filter-switch"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bartenders Grid */}
      {filteredBartenders.length === 0 ? (
        <Card className="bg-white/5 backdrop-blur-md border border-white/5">
          <CardContent className="p-12 text-center">
            <User className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No bartenders found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || experienceFilter !== 'all' || locationFilter !== 'all' || availableTonightOnly
                ? 'Try adjusting your search criteria'
                : 'No bartender profiles available at the moment'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBartenders.map((bartender) => (
            <Card 
              key={bartender.id} 
              className="group bg-white/5 backdrop-blur-md border border-white/5 hover:border-white/20 transition-all duration-300 hover:shadow-lg"
              data-testid={`bartender-card-${bartender.id}`}
            >
              <CardContent className="p-6">
                {/* Avatar and Basic Info */}
                <div className="flex items-center mb-4">
                  <Avatar className="h-16 w-16 mr-4">
                    <AvatarImage 
                      src={bartender.user?.profile_picture} 
                      alt={`${bartender.user?.first_name} ${bartender.user?.last_name}`}
                    />
                    <AvatarFallback className="bg-primary/10 text-primary text-lg">
                      {bartender.user?.first_name?.[0]}{bartender.user?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white text-lg">
                      {bartender.user?.first_name || 'Bartender'}
                    </h3>
                    {bartender.verification_status === 'approved' && (
                      <Badge className="mb-1 bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-testid={`verified-badge-${bartender.id}`}>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Verified Bartender
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Experience and Rating */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 mr-1 text-[#F5C15D] fill-current" />
                      <span className="text-sm font-medium">
                        {bartender.average_rating ? bartender.average_rating.toFixed(1) : 'N/A'}
                      </span>
                      <span className="text-xs text-muted-foreground ml-1">
                        ({bartender.total_reviews || 0})
                      </span>
                    </div>
                    
                    {/* Distance Badge */}
                    {bartender.distance !== undefined && sortByDistance && (
                      <Badge variant="secondary" className="bg-primary/20 text-primary border-primary/30 text-xs">
                        <MapPin className="mr-1 h-3 w-3" />
                        {bartender.distance < 1 
                          ? '<1 mi' 
                          : `${bartender.distance.toFixed(1)} mi`
                        }
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex items-center text-sm text-muted-foreground mb-3" data-testid={`bartender-location-${bartender.id}`}>
                  <MapPin className="h-3 w-3 mr-1" />
                  {bartender.location?.city
                    ? `${bartender.location.city}, ${bartender.location.state}`
                    : 'Location not specified'}
                </div>

                <div className="flex items-center text-sm text-muted-foreground mb-4" data-testid={`bartender-experience-${bartender.id}`}>
                  <Award className="h-4 w-4 mr-1 text-primary" />
                  <span className="font-medium">{bartender.experience_years || 0} years experience</span>
                </div>

                {/* Skills */}
                {bartender.skills && bartender.skills.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {bartender.skills.slice(0, 4).map((skill, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                      {bartender.skills.length > 4 && (
                        <Badge variant="outline" className="text-xs">
                          +{bartender.skills.length - 4} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {bartender.available_tonight && (
                  <Badge className="mb-4 bg-green-500/20 text-green-400 border-green-500/30" data-testid={`available-tonight-badge-${bartender.id}`}>
                    Available Tonight
                  </Badge>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleViewProfile(bartender.user_id)}
                    variant="outline"
                    size="sm"
                      className="flex-1 min-h-[48px]"
                    data-testid={`view-profile-btn-${bartender.id}`}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Profile
                  </Button>
                  {showOwnerActions ? (
                    <>
                      <Button
                        onClick={() => toggleSaveBartender(bartender.user_id)}
                        size="sm"
                        variant="outline"
                        className="min-h-[48px]"
                        disabled={Boolean(savingBartenderIds[bartender.user_id])}
                        data-testid={`save-bartender-btn-${bartender.user_id}`}
                      >
                        {savedBartenderIds.includes(bartender.user_id) ? (
                          <><BookmarkCheck className="h-4 w-4 mr-2" />Saved</>
                        ) : (
                          <><Bookmark className="h-4 w-4 mr-2" />Save</>
                        )}
                      </Button>
                      <Button 
                        onClick={() => handleSendMessage(bartender.user_id)}
                        size="sm"
                        className="flex-1 bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] min-h-[48px]"
                        data-testid={`message-btn-${bartender.id}`}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Message
                      </Button>
                    </>
                  ) : (
                    <div className="flex-1">
                      {isBarOwner ? (
                        <Button
                          onClick={() => navigate('/owner/browse-bartenders')}
                          size="sm"
                          className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] min-h-[48px]"
                          data-testid={`open-owner-marketplace-btn-${bartender.id}`}
                        >
                          Open Owner Marketplace
                        </Button>
                      ) : (
                        <>
                          <Button
                            onClick={() => navigate('/register')}
                            size="sm"
                            className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] min-h-[48px]"
                            data-testid={`unlock-contact-btn-${bartender.id}`}
                          >
                            Create Free Owner Account
                          </Button>
                          <p className="mt-2 text-xs text-muted-foreground" data-testid={`unlock-contact-supporting-text-${bartender.id}`}>
                            Owner accounts can save favorites and message bartenders directly.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default BartendersPage;
